import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft, MapPin, Users, BedDouble, Bath, MessageCircle, Check,
  Wifi, Car, UtensilsCrossed, Snowflake, LogIn, LogOut, ShieldCheck,
} from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { VACATION_WHATSAPP, waLinkTo } from "@/lib/brand";
import useSEO from "@/hooks/useSEO";

const SERVICE_ICON = (name) => {
  const n = name.toLowerCase();
  if (n.includes("wi")) return Wifi;
  if (n.includes("parch")) return Car;
  if (n.includes("cucin")) return UtensilsCrossed;
  if (n.includes("aria") || n.includes("condiz")) return Snowflake;
  return Check;
};

const pin = L.divIcon({
  className: "casamore-pin",
  html: `<div style="transform:translate(-50%,-100%)"><span style="display:block;width:16px;height:16px;background:#0F5C4A;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 12px rgba(15,92,74,.4)"></span></div>`,
  iconSize: [0, 0],
});

export default function VacanzaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
    api.get(`/public/vacation-rentals/${id}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useSEO({
    title: s ? `${s.nome} — ${s.localita} · Casa Vacanza in Puglia | Casamore Group` : "Casa Vacanza in Puglia | Casamore Group",
    description: s ? (s.descrizione || `${s.nome} a ${s.localita}. Affitto breve in Puglia con Casamore Group.`).slice(0, 155) : "Affitti brevi e case vacanza in Puglia con Casamore Group.",
    canonical: `${window.location.origin}/vacanze/${id}`,
  });

  if (notFound) return (
    <div className="pt-40 pb-32 text-center">
      <p className="text-anthracite/60">Struttura non trovata.</p>
      <Link to="/vacanze" className="text-brand font-semibold">Torna alle strutture</Link>
    </div>
  );
  if (!s) return <div className="pt-40 pb-32 text-center text-anthracite/40">Caricamento…</div>;

  const imgs = (s.immagini || []).map(fileUrl);
  const center = [s.coords?.lat || 40.9, s.coords?.lng || 16.9];
  const waMsg = `Salve, sono interessato alla struttura "${s.nome}" (${s.localita}). Vorrei informazioni e disponibilità.`;

  const facts = [
    { icon: Users, label: "Posti letto", value: s.postiLetto || s.maxOspiti || "—" },
    { icon: BedDouble, label: "Camere", value: s.camere || "—" },
    { icon: Bath, label: "Bagni", value: s.bagni || "—" },
  ];

  return (
    <div className="pt-24 pb-24">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
        <button onClick={() => navigate("/vacanze")} className="inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-brand transition-colors mb-6" data-testid="vac-back"><ArrowLeft className="h-4 w-4" /> Tutte le strutture</button>

        {/* Gallery */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-3 rounded-3xl overflow-hidden aspect-[16/10]">
            <img src={imgs[active]} alt={s.nome} className="w-full h-full object-cover" data-testid="vac-gallery-main" />
          </div>
          <div className="lg:col-span-1 grid grid-cols-4 lg:grid-cols-1 gap-3">
            {imgs.slice(0, 4).map((img, i) => (
              <button key={`${img}-${i}`} onClick={() => setActive(i)} className={`rounded-2xl overflow-hidden aspect-square lg:aspect-[16/10] ${active === i ? "ring-2 ring-brand" : ""}`} data-testid={`vac-thumb-${i}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold text-anthracite tracking-tight">{s.nome}</h1>
              <p className="mt-2 flex items-center gap-1.5 text-anthracite/60"><MapPin className="h-4 w-4 text-brand" /> {s.localita}{s.zona ? ` — ${s.zona}` : ""}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                {facts.map((f) => (
                  <span key={f.label} className="inline-flex items-center gap-2 rounded-full bg-white border border-border-subtle px-4 py-2 text-sm text-anthracite/80"><f.icon className="h-4 w-4 text-brand" /> {f.value} {f.label.toLowerCase()}</span>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-3">Descrizione</h2>
              <p className="text-anthracite/70 leading-relaxed whitespace-pre-line">{s.descrizione}</p>
            </div>

            {s.servizi?.length > 0 && (
              <div>
                <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Servizi disponibili</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {s.servizi.map((srv) => {
                    const Icon = SERVICE_ICON(srv);
                    return (
                      <div key={srv} className="flex items-center gap-3 rounded-xl bg-accent-subtle px-4 py-3">
                        <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand text-white shrink-0"><Icon className="h-4 w-4" /></span>
                        <span className="text-sm font-medium text-anthracite">{srv}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* House rules */}
            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Regole della casa</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-white px-4 py-3">
                  <LogIn className="h-5 w-5 text-brand" /><div><p className="text-xs text-anthracite/50">Check-in</p><p className="font-semibold text-anthracite">{s.checkIn || "dalle 15:00"}</p></div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-white px-4 py-3">
                  <LogOut className="h-5 w-5 text-brand" /><div><p className="text-xs text-anthracite/50">Check-out</p><p className="font-semibold text-anthracite">{s.checkOut || "entro le 10:00"}</p></div>
                </div>
                {s.regole && (
                  <div className="sm:col-span-2 flex items-start gap-3 rounded-xl border border-border-subtle bg-white px-4 py-3">
                    <ShieldCheck className="h-5 w-5 text-brand mt-0.5" /><div><p className="text-xs text-anthracite/50">Note</p><p className="text-anthracite">{s.regole}</p></div>
                  </div>
                )}
              </div>
            </div>

            {/* Map */}
            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Posizione</h2>
              <div className="rounded-3xl overflow-hidden border border-border-subtle">
                <MapContainer center={center} zoom={13} scrollWheelZoom={false} style={{ height: "360px", width: "100%" }}>
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" attribution='&copy; OpenStreetMap' />
                  <Marker position={center} icon={pin} />
                </MapContainer>
              </div>
              <p className="mt-2 text-xs text-anthracite/50">Posizione indicativa della zona.</p>
            </div>
          </div>

          {/* Sticky booking */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-3xl border border-border-subtle bg-white p-6 shadow-[0_20px_50px_-30px_rgba(15,92,74,0.4)]">
              {s.prezzoNotte ? <p className="font-display text-4xl font-semibold text-brand">€ {s.prezzoNotte}<span className="text-base text-anthracite/50 font-sans"> /notte</span></p> : <p className="font-display text-2xl font-semibold text-anthracite">Richiedi disponibilità</p>}
              <p className="text-sm text-anthracite/60 mt-2">Fino a {s.postiLetto || s.maxOspiti || "—"} ospiti · {s.bagni || "—"} bagni</p>
              <a href={waLinkTo(VACATION_WHATSAPP, waMsg)} target="_blank" rel="noopener noreferrer" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors" data-testid="vac-whatsapp-top">
                <MessageCircle className="h-4 w-4" /> Contattaci su WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Bottom WhatsApp CTA */}
        <div className="mt-16 rounded-3xl bg-brand text-white px-8 py-10 sm:px-12 text-center">
          <h3 className="font-display text-3xl sm:text-4xl">Ti interessa questa struttura?</h3>
          <p className="mt-2 text-white/80">Scrivici su WhatsApp per informazioni e disponibilità.</p>
          <a href={waLinkTo(VACATION_WHATSAPP, waMsg)} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand font-semibold px-8 py-4 transition-transform hover:scale-[1.03]" data-testid="vac-whatsapp-bottom">
            <MessageCircle className="h-5 w-5" /> Contattaci su WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
